/**
 * Validation Constants
 * Common validation patterns and messages
 */

export interface ValidationPattern {
  pattern: RegExp;
  message: string;
}

/**
 * Common validation patterns
 */
export const VALIDATION_PATTERNS: Record<string, ValidationPattern> = {
  email: {
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: 'Invalid email format'
  },
  phone: {
    pattern: /^[0-9]{10}$/,
    message: 'Phone must be 10 digits'
  },
  mobile: {
    pattern: /^[6-9][0-9]{9}$/,
    message: 'Invalid mobile number (must start with 6-9)'
  },
  pincode: {
    pattern: /^[0-9]{6}$/,
    message: 'Pincode must be 6 digits'
  },
  pan: {
    pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
    message: 'Invalid PAN format (e.g., ABCDE1234F)'
  },
  gstin: {
    pattern: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
    message: 'Invalid GSTIN format'
  },
  aadhar: {
    pattern: /^[0-9]{12}$/,
    message: 'Aadhar must be 12 digits'
  },
  ifsc: {
    pattern: /^[A-Z]{4}0[A-Z0-9]{6}$/,
    message: 'Invalid IFSC code format'
  },
  url: {
    pattern: /^https?:\/\/.+/,
    message: 'Invalid URL (must start with http:// or https://)'
  },
  alphanumeric: {
    pattern: /^[a-zA-Z0-9]+$/,
    message: 'Only letters and numbers allowed'
  },
  alphabetic: {
    pattern: /^[a-zA-Z\s]+$/,
    message: 'Only letters allowed'
  },
  numeric: {
    pattern: /^[0-9]+$/,
    message: 'Only numbers allowed'
  },
  decimal: {
    pattern: /^[0-9]+(\.[0-9]+)?$/,
    message: 'Must be a valid decimal number'
  },
  positiveNumber: {
    pattern: /^[1-9][0-9]*$/,
    message: 'Must be a positive number'
  },
  username: {
    pattern: /^[a-zA-Z0-9_]{3,20}$/,
    message: 'Username must be 3-20 characters (letters, numbers, underscore)'
  },
  strongPassword: {
    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
    message: 'Password must be 8+ characters with uppercase, lowercase, number and special character'
  },
};

/**
 * Get validation pattern by name
 */
export function getValidationPattern(patternName: string): ValidationPattern | undefined {
  return VALIDATION_PATTERNS[patternName.toLowerCase()];
}

/**
 * Get all pattern names
 */
export function getPatternNames(): string[] {
  return Object.keys(VALIDATION_PATTERNS);
}
